import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { onboardingApi, type OnboardingData } from "@/api/onboarding";
import { companiesApi } from "@/api/companies";
import { apiFetch } from "@/api/client";
import {
  ArrowLeft, ArrowRight, Building2, Globe, Users, Monitor,
  Loader2, CheckCircle2, Plus, X, Sparkles, AlertCircle,
} from "lucide-react";

const TOTAL_STEPS = 5;

const DEPARTMENT_SUGGESTIONS = [
  "Executive Leadership", "Finance", "Human Resources", "IT / Technology",
  "Marketing", "Sales", "Operations", "Customer Service",
  "Legal / Compliance", "Product / Engineering",
];

const IT_SYSTEMS: Record<string, string[]> = {
  Productivity: ["Microsoft 365", "Google Workspace", "Slack", "Teams"],
  CRM: ["Salesforce", "HubSpot", "Dynamics 365"],
  ERP: ["SAP", "Oracle", "NetSuite"],
  Dev: ["GitHub", "GitLab", "Jira", "Azure DevOps"],
  Cloud: ["Azure", "AWS", "GCP"],
};

const RESEARCH_PHASES = [
  "Analysing your organisation...",
  "Researching industry context...",
  "Building company profile...",
  "Finalising...",
];

interface UserInfo {
  name?: string;
  email?: string;
}

export function CustomerOnboarding() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRerun = searchParams.get("rerun") === "1";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingData | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [user, setUser] = useState<UserInfo>({});

  // Step 2
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [brandGuidelines, setBrandGuidelines] = useState("");

  // Step 3
  const [departments, setDepartments] = useState<string[]>([]);
  const [customDept, setCustomDept] = useState("");

  // Step 4
  const [itSystems, setItSystems] = useState<string[]>([]);
  const [customSystem, setCustomSystem] = useState("");

  // Step 5
  const [researchPhase, setResearchPhase] = useState(0);
  const [researchStatus, setResearchStatus] = useState<string | null>(null);
  const [researchError, setResearchError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phaseRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const populateFromData = useCallback((data: OnboardingData) => {
    setWebsiteUrl(data.websiteUrl ?? "");
    setBusinessDescription(data.businessDescription ?? "");
    setBrandGuidelines(data.brandGuidelinesContent ?? "");
    setDepartments(data.departments ?? []);
    setItSystems(data.itSystems ?? []);
  }, []);

  // Initial load
  useEffect(() => {
    if (!tenantId) return;
    const load = async () => {
      try {
        const [company, userData] = await Promise.all([
          companiesApi.get(tenantId),
          apiFetch<UserInfo>("/me"),
        ]);
        setCompanyName(company.name);
        setUser(userData);

        try {
          const data = await onboardingApi.getStatus(tenantId);
          setOnboarding(data);
          populateFromData(data);
          if (data.status === "complete" && !isRerun) {
            navigate(`/tenants/${tenantId}/dashboard`, { replace: true });
            return;
          }
          if (data.status !== "complete" && data.currentStep > 1) setStep(data.currentStep);
        } catch {
          // No onboarding record yet, start fresh
        }
      } catch {
        // Couldn't load company
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId, navigate, populateFromData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (phaseRef.current) clearInterval(phaseRef.current);
    };
  }, []);

  const saveStep = async (stepNumber: number) => {
    if (!tenantId) return;
    setSaving(true);
    try {
      let data: Record<string, unknown> = {};
      if (stepNumber === 2) {
        data = { websiteUrl, businessDescription, brandGuidelinesContent: brandGuidelines };
      } else if (stepNumber === 3) {
        data = { departments };
      } else if (stepNumber === 4) {
        data = { itSystems };
      }
      const result = await onboardingApi.saveStep(tenantId, stepNumber, data);
      setOnboarding(result);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (step >= 2 && step <= 4) await saveStep(step);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target?.result as string;
      setBrandGuidelines(content);
    };
    reader.readAsText(file);
  };

  const toggleDepartment = (dept: string) => {
    setDepartments((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
    );
  };

  const addCustomDept = () => {
    const trimmed = customDept.trim();
    if (trimmed && !departments.includes(trimmed)) {
      setDepartments((prev) => [...prev, trimmed]);
      setCustomDept("");
    }
  };

  const toggleSystem = (sys: string) => {
    setItSystems((prev) =>
      prev.includes(sys) ? prev.filter((s) => s !== sys) : [...prev, sys]
    );
  };

  const addCustomSystem = () => {
    const trimmed = customSystem.trim();
    if (trimmed && !itSystems.includes(trimmed)) {
      setItSystems((prev) => [...prev, trimmed]);
      setCustomSystem("");
    }
  };

  // Step 5: trigger research
  const startResearch = useCallback(async () => {
    if (!tenantId) return;
    setResearchStatus("researching");
    setResearchError(null);
    setResearchPhase(0);

    try {
      await onboardingApi.triggerResearch(tenantId);
    } catch {
      setResearchError("Failed to start research. Please try again.");
      setResearchStatus("failed");
      return;
    }

    // Cycle through phases
    phaseRef.current = setInterval(() => {
      setResearchPhase((p) => (p < RESEARCH_PHASES.length - 1 ? p + 1 : p));
    }, 4000);

    // Poll status
    pollRef.current = setInterval(async () => {
      try {
        const res = await onboardingApi.getResearchStatus(tenantId);
        if (res.status === "complete") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (phaseRef.current) clearInterval(phaseRef.current);
          setResearchStatus("complete");
          await onboardingApi.complete(tenantId);
          setTimeout(() => navigate(`/tenants/${tenantId}/dashboard`), 1500);
        } else if (res.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (phaseRef.current) clearInterval(phaseRef.current);
          setResearchStatus("failed");
          setResearchError(res.researchError ?? "Research failed. Please try again.");
        }
      } catch {
        // Keep polling
      }
    }, 2000);
  }, [tenantId, navigate]);

  useEffect(() => {
    if (step === 5 && researchStatus === null) {
      startResearch();
    }
  }, [step, researchStatus, startResearch]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  const stepIcons = [Sparkles, Globe, Users, Monitor, CheckCircle2];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Step indicator */}
      <div className="border-b border-border bg-surface px-6 py-4">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => {
            const StepIcon = stepIcons[i];
            const stepNum = i + 1;
            const isActive = stepNum === step;
            const isComplete = stepNum < step;
            return (
              <div key={stepNum} className="flex items-center gap-2">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                    isActive
                      ? "bg-accent text-white"
                      : isComplete
                        ? "bg-accent/20 text-accent"
                        : "bg-surface-hover text-text-muted"
                  }`}
                >
                  {isComplete ? (
                    <CheckCircle2 size={18} />
                  ) : (
                    <StepIcon size={18} />
                  )}
                </div>
                {i < TOTAL_STEPS - 1 && (
                  <div
                    className={`hidden h-px w-12 sm:block lg:w-20 ${
                      isComplete ? "bg-accent" : "bg-border"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-accent/10">
                <Building2 className="h-10 w-10 text-accent" />
              </div>
              <h1 className="mb-2 text-3xl font-bold text-text-primary">
                Welcome to ProClaw
              </h1>
              {companyName && (
                <p className="mb-4 text-lg text-accent">{companyName}</p>
              )}
              <p className="mx-auto mb-2 max-w-md text-text-secondary">
                {user.name ? `Hi ${user.name}, let's` : "Let's"} get your
                organisation set up. We'll gather some information about your
                business to configure your AI agents.
              </p>
              <p className="mx-auto mb-8 max-w-md text-sm text-text-muted">
                This takes about 2 minutes. We'll ask about your organisation,
                departments, and the systems you use.
              </p>
              <Button size="lg" onClick={handleNext}>
                Let's get started
                <ArrowRight size={18} />
              </Button>
            </div>
          )}

          {/* Step 2: About Your Organisation */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Globe size={22} className="text-accent" />
                  About Your Organisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    Website URL
                  </label>
                  <Input
                    placeholder="https://yourcompany.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    What does your company do?
                  </label>
                  <textarea
                    className="flex min-h-[100px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                    placeholder="Describe your business, services, and target market..."
                    value={businessDescription}
                    onChange={(e) => setBusinessDescription(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    Brand Guidelines
                    <span className="ml-2 text-xs font-normal text-text-muted">
                      (optional - .txt, .md, or .pdf)
                    </span>
                  </label>
                  <input
                    type="file"
                    accept=".txt,.md,.pdf"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-text-secondary file:mr-4 file:rounded-lg file:border-0 file:bg-accent/10 file:px-4 file:py-2 file:text-sm file:font-medium file:text-accent hover:file:bg-accent/20"
                  />
                  {brandGuidelines && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-accent">
                      <CheckCircle2 size={14} />
                      Guidelines loaded ({brandGuidelines.length.toLocaleString()} characters)
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Organisation Structure */}
          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Users size={22} className="text-accent" />
                  Organisation Structure
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-text-secondary">
                  Select the departments in your organisation. This helps us
                  configure the right agents for your needs.
                </p>
                <div className="flex flex-wrap gap-2">
                  {DEPARTMENT_SUGGESTIONS.map((dept) => (
                    <button
                      key={dept}
                      onClick={() => toggleDepartment(dept)}
                      className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                        departments.includes(dept)
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border text-text-secondary hover:border-text-muted"
                      }`}
                    >
                      {dept}
                    </button>
                  ))}
                </div>

                {/* Custom departments */}
                {departments
                  .filter((d) => !DEPARTMENT_SUGGESTIONS.includes(d))
                  .map((dept) => (
                    <div
                      key={dept}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-accent bg-accent/10 px-3 py-1.5 text-sm text-accent"
                    >
                      {dept}
                      <button onClick={() => toggleDepartment(dept)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                <div className="flex gap-2">
                  <Input
                    placeholder="Add a custom department..."
                    value={customDept}
                    onChange={(e) => setCustomDept(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCustomDept()}
                  />
                  <Button variant="outline" onClick={addCustomDept}>
                    <Plus size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: IT Systems */}
          {step === 4 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Monitor size={22} className="text-accent" />
                  IT Systems
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-text-secondary">
                  Which systems does your organisation use? This helps us
                  configure integrations and agent capabilities.
                </p>
                {Object.entries(IT_SYSTEMS).map(([category, systems]) => (
                  <div key={category}>
                    <h4 className="mb-2 text-sm font-medium text-text-muted">
                      {category}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {systems.map((sys) => (
                        <button
                          key={sys}
                          onClick={() => toggleSystem(sys)}
                          className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                            itSystems.includes(sys)
                              ? "border-accent bg-accent/10 text-accent"
                              : "border-border text-text-secondary hover:border-text-muted"
                          }`}
                        >
                          {sys}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Custom systems */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-text-muted">
                    Other
                  </h4>
                  <div className="mb-2 flex flex-wrap gap-2">
                    {itSystems
                      .filter(
                        (s) =>
                          !Object.values(IT_SYSTEMS).flat().includes(s)
                      )
                      .map((sys) => (
                        <div
                          key={sys}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-accent bg-accent/10 px-3 py-1.5 text-sm text-accent"
                        >
                          {sys}
                          <button onClick={() => toggleSystem(sys)}>
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a custom system..."
                      value={customSystem}
                      onChange={(e) => setCustomSystem(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && addCustomSystem()
                      }
                    />
                    <Button variant="outline" onClick={addCustomSystem}>
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 5: Processing */}
          {step === 5 && (
            <div className="text-center">
              {researchStatus === "complete" ? (
                <>
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-green-500/10">
                    <CheckCircle2 className="h-10 w-10 text-green-500" />
                  </div>
                  <h2 className="mb-2 text-2xl font-bold text-text-primary">
                    All done!
                  </h2>
                  <p className="text-text-secondary">
                    Redirecting to your dashboard...
                  </p>
                </>
              ) : researchStatus === "failed" ? (
                <>
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-danger/10">
                    <AlertCircle className="h-10 w-10 text-danger" />
                  </div>
                  <h2 className="mb-2 text-2xl font-bold text-text-primary">
                    Something went wrong
                  </h2>
                  <p className="mb-6 text-sm text-text-secondary">
                    {researchError}
                  </p>
                  <Button onClick={startResearch}>
                    <Sparkles size={16} />
                    Retry
                  </Button>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-accent/10">
                    <Loader2 className="h-10 w-10 animate-spin text-accent" />
                  </div>
                  <h2 className="mb-2 text-2xl font-bold text-text-primary">
                    Setting up your workspace
                  </h2>
                  <p className="mb-8 text-text-secondary">
                    {RESEARCH_PHASES[researchPhase]}
                  </p>
                  <div className="mx-auto max-w-xs">
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface-hover">
                      <div
                        className="h-full rounded-full bg-accent transition-all duration-1000"
                        style={{
                          width: `${((researchPhase + 1) / RESEARCH_PHASES.length) * 100}%`,
                        }}
                      />
                    </div>
                    <div className="mt-3 flex justify-between text-xs text-text-muted">
                      {RESEARCH_PHASES.map((_, i) => (
                        <div
                          key={i}
                          className={`h-1.5 w-1.5 rounded-full ${
                            i <= researchPhase ? "bg-accent" : "bg-surface-hover"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Navigation */}
          {step < 5 && (
            <div className="mt-6 flex items-center justify-between">
              {step > 1 ? (
                <Button variant="ghost" onClick={handleBack}>
                  <ArrowLeft size={16} />
                  Back
                </Button>
              ) : (
                <div />
              )}
              <Button onClick={handleNext} disabled={saving}>
                {saving && <Loader2 size={16} className="animate-spin" />}
                {step === 4 ? "Finish" : "Next"}
                <ArrowRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
